import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { usStatesData } from '../../mocks/usStates';
import masterData from '../../data/master_data.json';
import StateCalculatorInput from './components/StateCalculatorInput';
import StateResultsPanel from './components/StateResultsPanel';
import StateTaxVisualization from './components/StateTaxVisualization';
import StateFAQSection from './components/StateFAQSection';
import StateNearbyCities from './components/StateNearbyCities';
import StateCityGrid from './components/StateCityGrid';
import StateRelatedArticles from './components/StateRelatedArticles';

export interface StateTaxResult {
  grossSalary: number;
  federalTax: number;
  stateTax: number;
  socialSecurity: number;
  medicare: number;
  totalTax: number;
  netSalary: number;
  effectiveRate: number;
  breakdown: Array<{
    category: string;
    amount: number;
    rate: number;
    details?: string;
  }>;
}

const calculateStateTax = (
  grossSalary: number,
  stateSlug: string,
  filingStatus: string
): StateTaxResult => {
  const usa = (masterData as any).countries.usa;
  const federalTax = usa.federal_tax;
  const breakdown: StateTaxResult['breakdown'] = [];

  // Federal tax
  const standardDeduction = federalTax.standard_deduction;
  const taxableIncome = Math.max(0, grossSalary - standardDeduction);
  let fedTax = 0;
  let prevMax = 0;
  for (const bracket of federalTax.brackets) {
    if (taxableIncome <= prevMax) break;
    const taxable = Math.min(taxableIncome, bracket.max) - Math.max(prevMax, bracket.min - 1);
    if (taxable > 0) fedTax += taxable * bracket.rate;
    prevMax = bracket.max;
  }
  breakdown.push({
    category: 'Federal Tax',
    amount: fedTax,
    rate: fedTax / grossSalary,
    details: `Taxable income: $${taxableIncome.toLocaleString()} (after $${standardDeduction.toLocaleString()} standard deduction)`
  });

  // Social Security
  const ssBase = Math.min(grossSalary, federalTax.social_security_cap);
  const ss = ssBase * federalTax.social_security_rate;
  breakdown.push({
    category: 'Social Security',
    amount: ss,
    rate: federalTax.social_security_rate,
    details: `6.2% on first $${federalTax.social_security_cap.toLocaleString()}`
  });

  // Medicare
  const med = grossSalary * federalTax.medicare_rate;
  breakdown.push({
    category: 'Medicare',
    amount: med,
    rate: federalTax.medicare_rate,
    details: '1.45% on all income'
  });

  // State tax
  let stateTaxAmount = 0;
  const stateInMaster = usa.states?.[stateSlug];
  if (stateInMaster?.state_tax?.brackets) {
    let sPrevMax = 0;
    for (const bracket of stateInMaster.state_tax.brackets) {
      if (grossSalary <= sPrevMax) break;
      const taxable = Math.min(grossSalary, bracket.max) - Math.max(sPrevMax, bracket.min - 1);
      if (taxable > 0) stateTaxAmount += taxable * bracket.rate;
      sPrevMax = bracket.max;
    }
  } else {
    // Estimate from usStatesData topRate
    const stateInfo = usStatesData.find(s => s.slug === stateSlug);
    if (stateInfo) {
      const topRate = parseFloat(stateInfo.topRate) / 100;
      stateTaxAmount = grossSalary * topRate * 0.75; // rough effective estimate
    }
  }

  if (stateTaxAmount > 0) {
    breakdown.push({
      category: 'State Tax',
      amount: stateTaxAmount,
      rate: stateTaxAmount / grossSalary,
      details: `${usStatesData.find(s => s.slug === stateSlug)?.name || 'State'} income tax`
    });
  }

  const totalTax = fedTax + ss + med + stateTaxAmount;
  const netSalary = grossSalary - totalTax;

  return {
    grossSalary,
    federalTax: fedTax,
    stateTax: stateTaxAmount,
    socialSecurity: ss,
    medicare: med,
    totalTax,
    netSalary,
    effectiveRate: totalTax / grossSalary,
    breakdown
  };
};

const StateDetailPage = () => {
  const { stateSlug } = useParams<{ stateSlug: string }>();
  const navigate = useNavigate();
  const [stateData, setStateData] = useState<any>(null);
  const [salary, setSalary] = useState<number>(75000);
  const [frequency, setFrequency] = useState<'annual' | 'monthly' | 'biweekly'>('annual');
  const [filingStatus, setFilingStatus] = useState<string>('single');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const state = usStatesData.find(s => s.slug === stateSlug);
    if (!state) {
      navigate('/states');
      return;
    }
    setStateData(state);
  }, [stateSlug, navigate]);

  const taxResult = useMemo(() => {
    if (!stateSlug) return null;
    const annualSalary =
      frequency === 'monthly' ? salary * 12 : frequency === 'biweekly' ? salary * 26 : salary;
    return calculateStateTax(annualSalary, stateSlug, filingStatus);
  }, [salary, frequency, filingStatus, stateSlug]);

  const formatCurrency = useCallback((amount: number) => {
    return `$${Math.round(amount).toLocaleString()}`;
  }, []);

  // Get calculator-enabled cities from master_data
  const calculatorCities = useMemo(() => {
    const stateInMasterData = Object.values((masterData as any).countries.usa.states || {}).find(
      (s: any) => s.slug === stateSlug
    ) as any;
    return stateInMasterData?.cities ? Object.keys(stateInMasterData.cities) : [];
  }, [stateSlug]);

  // Nearby states in same region
  const nearbyStates = useMemo(() => {
    if (!stateData) return [];
    return usStatesData
      .filter(s => s.region === stateData.region && s.slug !== stateData.slug)
      .slice(0, 6)
      .map(s => ({
        name: s.name,
        slug: s.slug,
        path: `/states/${s.slug}`,
        topRate: s.topRate,
        abbreviation: s.abbreviation
      }));
  }, [stateData]);

  const pageTitle = stateData
    ? `${stateData.name} Salary Tax Calculator 2026 - Take-Home Pay`
    : 'State Tax Calculator';
  const pageDescription = stateData
    ? `Calculate your exact take-home pay in ${stateData.name}. Free salary calculator with ${stateData.name} tax rates including federal, state taxes. ${stateData.stateTaxType} income tax with top rate of ${stateData.topRate}. Updated for 2026.`
    : 'State salary tax calculator';
  const canonicalUrl = `https://mysalarycalculator.net/states/${stateSlug}`;

  useDocumentMeta({
    title: pageTitle,
    description: pageDescription,
    keywords: stateData
      ? `${stateData.name} salary calculator, ${stateData.name} tax calculator, ${stateData.name} take-home pay, ${stateData.name} income tax, ${stateData.name} paycheck calculator 2026`
      : '',
    canonical: canonicalUrl,
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogUrl: canonicalUrl,
    ogImage: stateData
      ? `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%20%20stateData.imageQuery%20%20%20%20%20%20%20%20%20%29%7D&width=1200&height=630&seq=state-og-${stateData.slug}&orientation=landscape`
      : '',
    twitterCard: 'summary_large_image',
    twitterTitle: pageTitle,
    twitterDescription: pageDescription,
    twitterImage: stateData
      ? `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%20%20stateData.imageQuery%20%20%20%20%20%20%20%20%20%29%7D&width=1200&height=630&seq=state-twitter-${stateData.slug}&orientation=landscape`
      : '',
    structuredData: stateData
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://mysalarycalculator.net/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'US States',
                item: 'https://mysalarycalculator.net/states'
              },
              { '@type': 'ListItem', position: 3, name: stateData.name, item: canonicalUrl }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: pageTitle,
            description: pageDescription,
            url: canonicalUrl,
            mainEntity: {
              '@type': 'SoftwareApplication',
              name: `${stateData.name} Salary Calculator`,
              applicationCategory: 'FinanceApplication',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }
            }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: `How much tax do I pay in ${stateData.name}?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `${stateData.name} has a ${stateData.stateTaxType.toLowerCase()} income tax system with a top rate of ${stateData.topRate}. Use our calculator to see your exact take-home pay.`
                }
              },
              {
                '@type': 'Question',
                name: `Does ${stateData.name} have state income tax?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text:
                    stateData.topRate === '0%'
                      ? `No, ${stateData.name} does not have a state income tax. Residents only pay federal taxes.`
                      : `Yes, ${stateData.name} has a ${stateData.stateTaxType.toLowerCase()} income tax with a top marginal rate of ${stateData.topRate}.`
                }
              }
            ]
          }
        ]
      : []
  });

  if (!stateData || !taxResult) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calculator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" role="banner">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <a href="/" aria-label="Salary Tax Calculator Home">
                <img
                  src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
                  alt="Salary Tax Calculator Logo"
                  className="h-10 w-auto cursor-pointer"
                  width="40"
                  height="40"
                />
              </a>
              <div className="hidden md:block">
                <span className="text-xl font-bold text-gray-900">Salary Tax Calculator</span>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="/states"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block"
              >
                <i className="ri-map-pin-line mr-1"></i>US States
              </a>
              <a
                href="/blog"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block"
              >
                <i className="ri-article-line mr-1"></i>Tax Tips Blog
              </a>
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 transition-colors cursor-pointer"
                aria-label="Select language"
              >
                <option>🇺🇸 EN</option>
                <option>🇬🇧 EN-UK</option>
                <option>🇪🇸 ES</option>
                <option>🇫🇷 FR</option>
                <option>🇩🇪 DE</option>
              </select>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav className="flex items-center space-x-2 text-sm mb-8" aria-label="Breadcrumb">
            <a
              href="/"
              className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1"
            >
              <i className="ri-home-4-line text-xs"></i> Home
            </a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <a href="/states" className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">
              US States
            </a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">{stateData.name}</span>
          </nav>

          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="px-3 py-1 bg-gray-100 border border-gray-200 rounded-lg text-sm font-bold text-gray-800">
                {stateData.abbreviation}
              </span>
              <span className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-gray-500 text-xs">
                {stateData.region}
              </span>
              {stateData.topRate === '0%' && (
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold whitespace-nowrap">
                  No Income Tax
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <strong>{stateData.name} Salary &amp; Tax Calculator 2026</strong>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Calculate your take-home pay in {stateData.name} with a{' '}
              <strong>{stateData.stateTaxType.toLowerCase()} income tax</strong> system
              {stateData.topRate !== '0%' ? (
                <> and a top rate of <strong>{stateData.topRate}</strong></>
              ) : null}
              .<strong> Free, accurate, and updated for 2026.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <main role="main">
        <section className="py-12" aria-label="Salary calculator">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <article className="lg:col-span-3">
                <StateCalculatorInput
                  salary={salary}
                  frequency={frequency}
                  filingStatus={filingStatus}
                  showAdvanced={showAdvanced}
                  onSalaryChange={setSalary}
                  onFrequencyChange={setFrequency}
                  onFilingStatusChange={setFilingStatus}
                  onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
                />
              </article>
              <aside className="lg:col-span-2" aria-label="Calculation results">
                <StateResultsPanel taxResult={taxResult} frequency={frequency} formatCurrency={formatCurrency} />
              </aside>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <article>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                <strong>How {stateData.name} Income Tax &amp; Salary Deductions Work</strong>
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  {stateData.topRate === '0%'
                    ? `${stateData.name} is one of the few states that does not levy a state income tax. This means residents only pay federal income tax, Social Security, and Medicare — resulting in significantly higher take-home pay compared to most other states.`
                    : `${stateData.name} uses a ${stateData.stateTaxType.toLowerCase()} income tax system with a top marginal rate of ${stateData.topRate}. When combined with federal taxes, Social Security, and Medicare, understanding your total tax burden is essential for financial planning.`}
                </p>
                <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-r-lg">
                  <p>
                    <strong>Did you know?</strong> {stateData.name} has a population of {stateData.population}{' '}
                    with its capital in {stateData.capital}.
                    {stateData.topRate === '0%'
                      ? ` As a no-income-tax state, ${stateData.name} attracts workers and retirees seeking to maximize their earnings.`
                      : ` The ${stateData.stateTaxType.toLowerCase()} tax system means ${stateData.stateTaxType === 'Graduated' ? 'different portions of your income are taxed at different rates' : 'all taxable income is taxed at the same rate'}.`}
                  </p>
                </div>
                <p>
                  With {stateData.cities.length} major cities including{' '}
                  {stateData.cities.slice(0, 3).map((c: any) => c.name).join(', ')},
                  {stateData.name} offers diverse employment opportunities across multiple industries. Use our calculator above to see your personalized tax breakdown.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  <strong>Understanding Your {stateData.name} Paycheck &amp; Tax Withholdings</strong>
                </h3>
                <p>
                  When you receive your paycheck in {stateData.name}, several deductions are automatically taken out.
                  These include <strong>federal income tax</strong>
                  {stateData.topRate !== '0%' ? (
                    <> <strong>state income tax ({stateData.topRate} top rate)</strong>,</>
                  ) : null}{' '}
                  <strong>Social Security (6.2%)</strong>, and <strong>Medicare (1.45%)</strong>. Our calculator breaks down each of these components so you can see exactly where your money goes.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  <strong>{stateData.name} Federal &amp; State Tax Rates for 2026</strong>
                </h3>
                <p>
                  {stateData.topRate === '0%'
                    ? `Since ${stateData.name} has no state income tax, your only tax obligations are at the federal level. The 2026 federal tax brackets range from 10% to 37%, with a standard deduction of $13,850 for single filers.`
                    : `The ${stateData.name} ${stateData.stateTaxType.toLowerCase()} income tax has a top marginal rate of ${stateData.topRate}. Combined with federal taxes ranging from 10% to 37%, your effective tax rate depends on your total income, filing status, and deductions.`}
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* Tax Visualization */}
        <StateTaxVisualization
          taxResult={taxResult}
          stateName={stateData.name}
          stateSlug={stateSlug || ''}
          formatCurrency={formatCurrency}
        />

        {/* Cities Grid */}
        <StateCityGrid stateData={stateData} calculatorCities={calculatorCities} stateSlug={stateSlug || ''} />

        {/* Nearby States */}
        <StateNearbyCities stateName={stateData.name} region={stateData.region} nearbyStates={nearbyStates} />

        {/* FAQ Section */}
        <StateFAQSection
          stateName={stateData.name}
          stateData={stateData}
          salary={frequency === 'monthly' ? salary * 12 : frequency === 'biweekly' ? salary * 26 : salary}
          taxResult={taxResult}
        />

        {/* Related Blog Articles */}
        <StateRelatedArticles
          stateName={stateData.name}
          stateSlug={stateSlug || ''}
          region={stateData.region}
        />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="grid grid-cols-1 md:grid-cols-4 gap-8" aria-label="Footer navigation">
            <div>
              <h3 className="text-lg font-bold mb-4">Salary Calculator</h3>
              <p className="text-gray-400 text-sm">
                Accurate tax calculations for locations worldwide. Plan your finances with confidence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Popular Locations</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/usa/nj/wood-ridge" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">
                    Wood-Ridge, NJ
                  </a>
                </li>
                <li>
                  <a href="/usa/ny/manhattan" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">
                    Manhattan, NY
                  </a>
                </li>
                <li>
                  <a href="/uk/london" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">
                    London, UK
                  </a>
                </li>
                <li>
                  <a href="/usa/ca/san-francisco" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">
                    San Francisco, CA
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/states" className="hover:text-white transition-colors cursor-pointer">
                    US States
                  </a>
                </li>
                <li>
                  <a href="/blog" className="hover:text-white transition-colors cursor-pointer">
                    Tax Tips Blog
                  </a>
                </li>
                <li>
                  <a href="/sitemap" className="hover:text-white transition-colors cursor-pointer">
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/privacy" className="hover:text-white transition-colors cursor-pointer">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-white transition-colors cursor-pointer">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="/disclaimer" className="hover:text-white transition-colors cursor-pointer">
                    Disclaimer
                  </a>
                </li>
              </ul>
            </div>
          </nav>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>
              &copy; 2026 Salary Tax Calculator. All rights reserved. |{' '}
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StateDetailPage;
